import React from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ExpandableSection,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  InvestigationOverlap,
  InvestigationPreview,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationRule as Rule,
  MatchingPattern,
  RULE_FIELDS,
  excludedValues,
  investigationPath,
  ownedExclusion,
  predicatesToNarrow,
  resolutionReasonLabel,
} from "./investigationUtils";
import {
  OverlapRow,
  mostlyCoveredBy,
  relationLabel,
  sharedItemsLabel,
  withoutCancelled,
} from "./PreviewOverlaps";
import { OverlapChoice } from "./InvestigationOverlapDialog";
import { shortPatternId } from "./trackstarsText";

/**
 * Share of the draft a single unclaimed TrackStars pattern must explain before the page
 * recommends narrowing to it. Below that, narrowing would throw most of the population away.
 */
export const PATTERN_DOMINANT_SHARE = 0.5;

/** The one thing the preview recommends doing next. */
export type Verdict =
  | { kind: "open"; overlap: InvestigationOverlap }
  | { kind: "narrow"; pattern: MatchingPattern }
  | { kind: "create"; mode: "full" | "remainder" };

const titleOf = (o: InvestigationOverlap) =>
  o.title || o.investigationId || "Investigation";

const needsNarrowing = (rule: Rule, pattern: MatchingPattern) =>
  predicatesToNarrow(rule, (pattern.suggestion.derivedRule ?? []) as Rule)
    .length > 0;

/**
 * Picks the single recommended action, first match wins:
 * 1. every item is owned, or one case covers most of them → work on that case;
 * 2. one unclaimed TrackStars pattern explains most of a wider draft → narrow to it;
 * 3. otherwise → create, for the unowned remainder when anything overlaps.
 */
export function recommendAction(
  preview: InvestigationPreview,
  activeOverlaps: InvestigationOverlap[],
  freePatterns: MatchingPattern[],
  rule: Rule
): Verdict {
  const total = preview.count ?? 0;
  const unassigned = preview.unassignedCount ?? 0;
  const active = withoutCancelled(activeOverlaps);
  const byShare = [...active].sort(
    (a, b) => (b.overlappingCount ?? 0) - (a.overlappingCount ?? 0)
  );
  if (unassigned === 0 && byShare.length)
    return { kind: "open", overlap: byShare[0] };
  const mostly = mostlyCoveredBy(byShare, total);
  if (mostly) return { kind: "open", overlap: mostly };
  if (freePatterns.length === 1) {
    const [only] = freePatterns;
    if (
      needsNarrowing(rule, only) &&
      total > 0 &&
      (only.suggestion.totalMatches ?? 0) / total >= PATTERN_DOMINANT_SHARE
    )
      return { kind: "narrow", pattern: only };
  }
  const overlapping = active.length > 0 || (preview.assignedCount ?? 0) > 0;
  return { kind: "create", mode: overlapping ? "remainder" : "full" };
}

/** Long values (error messages) shortened so the note stays on a line or two. */
const clip = (text: string, max = 60) =>
  text.length > max ? `“${text.slice(0, max - 1)}…”` : `“${text}”`;

const plural = (n: number, noun: string) =>
  `${n.toLocaleString()} ${noun}${n === 1 ? "" : "s"}`;

/** One sentence (plus an optional second) explaining the verdict. */
export function verdictSentence(
  verdict: Verdict,
  preview: InvestigationPreview
): string[] {
  const total = preview.count ?? 0;
  const unassigned = preview.unassignedCount ?? 0;
  const assigned = preview.assignedCount ?? 0;
  switch (verdict.kind) {
    case "open":
      return unassigned === 0
        ? [
            `Every matching item already belongs to another case, mostly “${titleOf(
              verdict.overlap
            )}”.`,
            "Work on it there; nothing new can be created.",
          ]
        : [
            `“${titleOf(verdict.overlap)}” already covers ${sharedItemsLabel(
              verdict.overlap.overlappingCount,
              total
            )}.`,
            "Work on it there: new matching items go to it automatically.",
          ];
    case "narrow": {
      const s = verdict.pattern.suggestion;
      return [
        `TrackStars ${shortPatternId(s.patternId)} explains ${(
          s.totalMatches ?? 0
        ).toLocaleString()} of these ${total.toLocaleString()} items and nobody works it yet.`,
        "Narrow the rule to it so the case covers one known pattern.",
      ];
    }
    default:
      if (verdict.mode === "full")
        return [`All ${plural(total, "matching item")} are unowned.`];
      return [
        `${unassigned.toLocaleString()} of ${plural(
          total,
          "item"
        )} are unowned.`,
        assigned > 0
          ? `${plural(assigned, "item")} already belong${
              assigned === 1 ? "s" : ""
            } to other cases; they stay there.`
          : "Other cases use an overlapping rule but own none of these yet.",
      ];
  }
}

interface PreviewVerdictProps {
  stage: Stage;
  preview: InvestigationPreview;
  /** Active overlaps, already without the split parent. */
  activeOverlaps: InvestigationOverlap[];
  rule: Rule;
  /** TrackStars patterns the draft touches that no case works yet. */
  freePatterns: MatchingPattern[];
  /** investigationId → TrackStars pattern it works, for a badge on that overlap row. */
  patternByInvestigation: Record<string, string>;
  /** Unclaimed-pattern rows (with their own actions), shown inside the collapsed section. */
  trackStars?: React.ReactNode;
  /** Building a child of a split parent: create buttons say so. */
  splitMode?: boolean;
  createDisabled?: boolean;
  /** Unused since the verdict has no create button; kept so callers need not change. */
  // eslint-disable-next-line react/no-unused-prop-types
  createDisabledReason?: string;
  /** Unused since the verdict has no create button; kept so callers need not change. */
  // eslint-disable-next-line react/no-unused-prop-types
  creating?: boolean;
  onCreate: (choice: OverlapChoice) => void;
  onNarrow: (pattern: MatchingPattern) => void;
  onAdopt: (pattern: MatchingPattern) => void;
  /**
   * Applies the carve-out predicates (InvestigationOverlap.exclusion) to the draft rule so the
   * preview leaves out the items other cases own. Omitted: no "Exclude items owned by…" button.
   */
  onExcludeOwned?: (predicates: Rule) => void;
  /** Viewer alias: "Add to <case>" is offered only on a mostly-covering case they own. */
  currentUser?: string | null;
  /** Adds the previewed items to that case instead of creating a new one. */
  onAddTo?: (investigationId: string) => void;
  adding?: boolean;
  addError?: string;
}

/**
 * The preview's decision box: one sentence, one recommended button, everything else under
 * "Other options" and a collapsed "Overlaps" list. Replaces the stacked TrackStars and overlap
 * alerts, which made a 0.1% overlap look like a blocker.
 */
export const PreviewVerdict: React.FC<PreviewVerdictProps> = ({
  stage,
  preview,
  activeOverlaps,
  rule,
  freePatterns,
  patternByInvestigation,
  trackStars,
  splitMode,
  createDisabled,
  onCreate,
  onNarrow,
  onAdopt,
  onExcludeOwned,
  currentUser,
  onAddTo,
  adding,
  addError,
}) => {
  const active = [...withoutCancelled(activeOverlaps)].sort(
    (a, b) => (b.overlappingCount ?? 0) - (a.overlappingCount ?? 0)
  );
  const history = withoutCancelled(preview.historicalOverlaps);
  const total = preview.count ?? 0;
  const unassigned = preview.unassignedCount ?? 0;
  const verdict = recommendAction(preview, active, freePatterns, rule);
  // The viewer owns the case that already covers most of the draft: top it up rather than open it.
  const canAdd =
    verdict.kind === "open" &&
    unassigned > 0 &&
    !!onAddTo &&
    !!currentUser &&
    verdict.overlap.owner === currentUser;
  const [headline, sentenceDetail] = verdictSentence(verdict, preview);
  const detail = canAdd
    ? "It's yours: add these items to it instead of creating a near-duplicate."
    : sentenceDetail;
  const createMode: OverlapChoice["kind"] =
    active.length || (preview.assignedCount ?? 0) > 0 ? "remainder" : "full";
  const createCount = createMode === "remainder" ? unassigned : total;
  const createText = `${splitMode ? "Split out" : "Create"} for ${plural(
    createCount,
    "item"
  )}`;

  let primary: React.ReactNode;
  if (verdict.kind === "open" && canAdd) {
    const { overlap } = verdict;
    primary = (
      <Button
        variant="primary"
        loading={adding}
        onClick={() =>
          overlap.investigationId && onAddTo?.(overlap.investigationId)
        }
      >
        Add to “{titleOf(overlap)}”
      </Button>
    );
  } else if (verdict.kind === "open") {
    const title = titleOf(verdict.overlap);
    primary = (
      <Button
        variant="primary"
        href={investigationPath(verdict.overlap.investigationId || "", stage)}
        target="_blank"
        iconName="external"
        iconAlign="right"
        ariaLabel={`Open ${title} in a new tab`}
      >
        Open “{title}”
      </Button>
    );
  } else if (verdict.kind === "narrow") {
    primary = (
      <Button variant="primary" onClick={() => onNarrow(verdict.pattern)}>
        Narrow to {shortPatternId(verdict.pattern.suggestion.patternId)}
      </Button>
    );
  }
  // A "create" verdict gets no button here: the Create section below is the only create action
  // (its overlap dialog offers the unowned remainder when anything overlaps).

  // One click to take the other cases' items out of the rule itself, so the new case does not
  // overlap them at all (rather than only skipping the items they own today).
  const carve =
    onExcludeOwned && unassigned > 0 ? ownedExclusion(active) : null;
  // Which cases the carve-out separates this one from, by name when there is just one.
  const carved = carve
    ? active.filter((o) => o.exclusion?.length && o.investigationId)
    : [];
  const carveTarget =
    carved.length === 1
      ? `“${titleOf(carved[0])}”`
      : `${carved.length} other cases`;
  const excludeButton = carve && (
    <Button
      variant={primary ? "normal" : "primary"}
      data-testid="exclude-owned"
      onClick={() => onExcludeOwned?.(carve.predicates)}
    >
      Remove the overlap
    </Button>
  );
  // Says exactly what the click does: the condition it adds and what drops out of the preview.
  const excludeNote = carve && (
    <Box
      variant="small"
      color="text-body-secondary"
      data-testid="exclude-owned-note"
    >
      Adds{" "}
      {carve.predicates.map((p, i) => (
        <React.Fragment key={p.field as string}>
          {i > 0 && " and "}
          <strong>
            {RULE_FIELDS.find((f) => f.field === p.field)?.label ?? p.field} is
            not {clip(excludedValues(p).join(" or "))}
          </strong>
        </React.Fragment>
      ))}{" "}
      to your rule, so this case won&rsquo;t overlap {carveTarget}.{" "}
      {plural(carve.itemCount, "item")}
      {carve.extraCount > 0
        ? ` (+ ${plural(carve.extraCount, "other item")})`
        : ""}{" "}
      will drop out of the preview.
    </Box>
  );

  // Alternatives, in the order people reach for them.
  const items: ButtonDropdownProps.Item[] = [];
  if (verdict.kind !== "create" && createCount > 0 && !createDisabled)
    items.push({ id: "create", text: createText });
  if (!splitMode && !createDisabled)
    active
      .filter((o) => o.relation === "rule_subset" && o.investigationId)
      .forEach((o) =>
        items.push({
          id: `child:${o.investigationId ?? ""}`,
          text: `Split out of “${titleOf(o)}”`,
        })
      );
  freePatterns
    .filter(
      (p) =>
        !(verdict.kind === "narrow" && verdict.pattern === p) &&
        p.suggestion.patternId
    )
    .forEach((p) =>
      items.push({
        id: `pattern:${p.suggestion.patternId}`,
        text: needsNarrowing(rule, p)
          ? `Narrow to TrackStars ${shortPatternId(p.suggestion.patternId)}`
          : `Adopt TrackStars ${shortPatternId(
              p.suggestion.patternId
            )} as origin`,
      })
    );
  // No "Open <case>" items: the Overlaps list below already links every overlapping case.

  const onItemClick = (id: string) => {
    if (id === "create") onCreate({ kind: createMode });
    else if (id.startsWith("child:"))
      onCreate({
        kind: "child",
        parentInvestigationId: id.slice("child:".length),
      });
    else if (id.startsWith("pattern:")) {
      const p = freePatterns.find(
        (f) => f.suggestion.patternId === id.slice("pattern:".length)
      );
      if (p) (needsNarrowing(rule, p) ? onNarrow : onAdopt)(p);
    }
  };

  const overlapCount = active.length + history.length;
  const sectionParts = [
    active.length && plural(active.length, "active investigation"),
    history.length && `${history.length} resolved`,
    freePatterns.length &&
      plural(freePatterns.length, "unclaimed TrackStars pattern"),
  ].filter(Boolean);

  // Nothing overlaps and nothing to choose: the Unassigned count already says it all, so a
  // banner repeating "All N are unowned" only confuses.
  if (
    verdict.kind === "create" &&
    !primary &&
    !excludeButton &&
    items.length === 0 &&
    overlapCount === 0 &&
    freePatterns.length === 0 &&
    !addError
  )
    return null;

  return (
    <Alert
      type={verdict.kind === "open" ? "warning" : "info"}
      data-testid="preview-verdict"
      data-verdict={verdict.kind}
    >
      <SpaceBetween size="s">
        <div>
          <Box variant="p" fontWeight="bold">
            {headline}
          </Box>
          {detail && (
            <Box variant="p" color="text-body-secondary">
              {detail}
            </Box>
          )}
        </div>
        {addError && (
          <Box variant="p" color="text-status-error">
            {addError}
          </Box>
        )}
        {(primary || excludeButton || items.length > 0) && (
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            {primary}
            {excludeButton}
            {items.length > 0 && (
              <ButtonDropdown
                items={items}
                // The Alert clips overflow; render the menu in a portal so it isn't cut off.
                expandToViewport
                onItemClick={({ detail: d }) => onItemClick(d.id)}
              >
                Other options
              </ButtonDropdown>
            )}
          </SpaceBetween>
        )}
        {excludeNote}
        {(overlapCount > 0 || freePatterns.length > 0) && (
          <ExpandableSection
            variant="footer"
            headerText={`Overlaps ${sectionParts.join(" · ")}`}
            data-testid="preview-overlaps"
          >
            <SpaceBetween size="s">
              {active.map((o) => (
                <div key={o.investigationId}>
                  {o.investigationId &&
                    patternByInvestigation[o.investigationId] && (
                      <Badge color="blue">
                        TrackStars {patternByInvestigation[o.investigationId]}
                      </Badge>
                    )}{" "}
                  <OverlapRow
                    stage={stage}
                    overlap={o}
                    details={[
                      o.owner ? `owner ${o.owner}` : "needs an owner",
                      sharedItemsLabel(o.overlappingCount, total),
                      relationLabel(o.relation),
                    ]}
                  />
                </div>
              ))}
              {history.map((o) => (
                <OverlapRow
                  key={o.investigationId}
                  stage={stage}
                  overlap={o}
                  details={[
                    `Resolved: ${resolutionReasonLabel(o.resolutionReason)}`,
                    o.owner ? `by ${o.owner}` : "",
                    o.conclusion
                      ? `“${o.conclusion}”`
                      : "no conclusion recorded",
                  ]}
                />
              ))}
              {trackStars}
            </SpaceBetween>
          </ExpandableSection>
        )}
      </SpaceBetween>
    </Alert>
  );
};
